import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";

const OPTION_ROW_PX = 41;
const PANEL_GAP = 4;
const PANEL_PAD = 8;
const MAX_PANEL_PX = 200;

const getModalBox = (el) => {
  const modal = el?.closest("[data-help-manual-modal]");
  if (!modal) return null;
  const header = modal.querySelector("[data-help-manual-modal-header]");
  const box = modal.getBoundingClientRect();
  return {
    top: (header?.getBoundingClientRect().bottom ?? box.top) + PANEL_PAD,
    bottom: box.bottom - PANEL_PAD,
  };
};

const HelpManualSelect = ({
  label,
  required = false,
  value = "",
  options = [],
  onChange,
  error,
  placeholder = "- Select -",
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const buttonRef = useRef(null);
  const panelRef = useRef(null);
  const [panelStyle, setPanelStyle] = useState(null);

  const selected = options.find(
    (opt) => String(opt.value) === String(value)
  );

  const updatePlacement = () => {
    const btn = buttonRef.current;
    if (!btn) return;

    const rect = btn.getBoundingClientRect();
    const modal = getModalBox(btn);
    const topBound = modal?.top ?? PANEL_PAD;
    const bottomBound = modal?.bottom ?? window.innerHeight - PANEL_PAD;
    const desired = Math.min(
      MAX_PANEL_PX,
      Math.max(OPTION_ROW_PX, options.length * OPTION_ROW_PX)
    );
    const spaceBelow = bottomBound - rect.bottom - PANEL_GAP;
    const spaceAbove = rect.top - topBound - PANEL_GAP;
    const minDown = OPTION_ROW_PX * 2;
    const openAbove = spaceBelow < minDown && spaceAbove > spaceBelow;
    const available = Math.max(
      OPTION_ROW_PX,
      openAbove ? spaceAbove : spaceBelow
    );
    const maxHeight = Math.min(desired, available);
    const width = rect.width;
    const left = Math.min(
      Math.max(PANEL_PAD, rect.left),
      window.innerWidth - width - PANEL_PAD
    );

    setPanelStyle(
      openAbove
        ? {
            position: "fixed",
            left: `${left}px`,
            width: `${width}px`,
            top: "auto",
            bottom: `${window.innerHeight - rect.top + PANEL_GAP}px`,
            maxHeight: `${maxHeight}px`,
            zIndex: 60,
          }
        : {
            position: "fixed",
            left: `${left}px`,
            width: `${width}px`,
            top: `${rect.bottom + PANEL_GAP}px`,
            bottom: "auto",
            maxHeight: `${maxHeight}px`,
            zIndex: 60,
          }
    );
  };

  useLayoutEffect(() => {
    if (!open) {
      setPanelStyle(null);
      return undefined;
    }
    updatePlacement();
    const onReposition = () => updatePlacement();
    window.addEventListener("resize", onReposition);
    window.visualViewport?.addEventListener("resize", onReposition);
    window.addEventListener("scroll", onReposition, true);
    const frame = requestAnimationFrame(() => {
      panelRef.current
        ?.querySelector('[aria-selected="true"]')
        ?.scrollIntoView({ block: "nearest" });
    });
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", onReposition);
      window.visualViewport?.removeEventListener("resize", onReposition);
      window.removeEventListener("scroll", onReposition, true);
    };
  }, [open, options.length]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event) => {
      const target = event.target;
      if (
        wrapRef.current?.contains(target) ||
        panelRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
    };
    const onKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={wrapRef}>
      {label && (
        <label className="block text-sm font-medium mb-1">
          {label}
          {required && <span className="text-error"> *</span>}
        </label>
      )}

      <button
        type="button"
        ref={buttonRef}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={(e) => {
          if (disabled) return;
          e.stopPropagation();
          setOpen((prev) => !prev);
        }}
        className={`help-manual-select w-full border rounded-md px-3 py-2 text-sm input-field bg-white flex justify-between items-center ${
          error ? "input-error" : ""
        } ${disabled ? "bg-gray-100 cursor-not-allowed opacity-70" : ""}`}
      >
        <span
          className={`truncate ${selected ? "text-black" : "text-gray-400"}`}
        >
          {selected?.label || placeholder}
        </span>
        {open ? (
          <FiChevronUp size={16} className="shrink-0 ml-2 text-gray-500" />
        ) : (
          <FiChevronDown size={16} className="shrink-0 ml-2 text-gray-500" />
        )}
      </button>

      {open &&
        panelStyle &&
        createPortal(
          <div
            ref={panelRef}
            role="listbox"
            style={panelStyle}
            className="bg-white border border-gray-200 rounded-md shadow-sm overflow-y-auto menu-scrollbar"
            onClick={(e) => e.stopPropagation()}
          >
            {options.length === 0 ? (
              <div className="px-4 py-2.5 text-sm text-gray-500">
                No options available
              </div>
            ) : (
              options.map((opt) => {
                const isSelected = String(opt.value) === String(value);
                return (
                  <div
                    key={opt.value}
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => {
                      onChange(opt.value);
                      setOpen(false);
                    }}
                    className={`px-4 py-2.5 text-sm cursor-pointer border-b border-gray-200 last:border-b-0 whitespace-nowrap overflow-hidden text-ellipsis ${
                      isSelected
                        ? "bg-gray-100 font-medium"
                        : "hover:bg-gray-100"
                    }`}
                  >
                    {opt.label}
                  </div>
                );
              })
            )}
          </div>,
          document.body
        )}

      <div className="min-h-[16px]">
        {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
      </div>
    </div>
  );
};

export default HelpManualSelect;
